import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../button';
import { Icon } from '../primitives/Icon';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fired when the confirm button is pressed; close happens automatically afterward. */
  onConfirm: () => void;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render in destructive/danger styling. Defaults to false (brand styling). */
  danger?: boolean;
  className?: string;
}

/**
 * Atlas-styled confirmation dialog over the modal-overlay pattern from
 * `dialog.tsx`. Mirrors the JSX prototype's imperative open / onClose /
 * onConfirm API and the `danger` toggle that switches the icon
 * (shield-check ⇄ trash) and the confirm button styling.
 *
 * Backdrop click closes; ESC handling is delegated to the calling
 * `Modal` flow (consumers compose this where ESC handling is in scope).
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  className,
}: ConfirmDialogProps) {
  if (!open) return null;
  return (
    <div
      role="presentation"
      onClick={onClose}
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        'bg-black/40 backdrop-blur-md dark:bg-black/60',
        'animate-in fade-in-0 duration-hover',
        className,
      )}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative flex w-[420px] max-w-full flex-col items-center gap-3 rounded-xl border border-border bg-bg-surface p-6 text-center shadow-xl',
          'animate-in zoom-in-[0.97] fade-in-0 duration-hover',
        )}
      >
        <div
          className={cn(
            'inline-flex h-12 w-12 items-center justify-center rounded-full',
            danger
              ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200'
              : 'bg-bg-brand-soft text-text-brand',
          )}
        >
          <Icon name={danger ? 'trash' : 'shieldCheck'} size={20} />
        </div>
        <div className="text-h3 text-text-primary">{title}</div>
        {message ? <div className="text-body text-text-secondary">{message}</div> : null}
        <div className="mt-2 flex w-full items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={danger ? 'danger' : 'primary'}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
